<template>
    <div class="idea-rule">
        <custom-search-resource-select
            :custom-filter="filterIdeas"
            :value="selectedIdea"
            :options="ideaOptions"
            :loading="loadingResources"
            field="ideaId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :disabled="readOnly"
            :placeholder="$t('ideaRule.Select Idea')"
            @input="$emit('input', $event)">
            <template #noData>
                <div class="no-data-message">
                    {{ $t('ideaRule.No Ideas found.') }}
                </div>
            </template>
        </custom-search-resource-select>
        <template v-if="selectedIdea">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                field="hasVoted"
                :value="selectedHasVoted"
                :options="hasVotedOptions"
                :preselect-first="true"
                :searchable="false"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <template v-if="selectedHasVoted.hasVoted">
                <simple-select
                    field="importance"
                    :value="selectedImportance"
                    :options="importanceOptions"
                    :preselect-first="true"
                    :searchable="false"
                    :disabled="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="$emit('input', $event)" />
            </template>

            <simple-select
                field="time"
                :value="selectedTime"
                :options="timeOptions"
                :preselect-first="true"
                :searchable="false"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />

            <component
                :is="dateComponent.component"
                v-if="dateComponent"
                v-bind="dateComponent.props"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import CustomSearchResourceSelect from '@/segments/components/segment-editor-vue/components/custom-search-resource-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import {
    getAbsoluteDateRangeConfig,
    getRelativeDateRangeConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range';

export default {
    name: 'IdeaRule',
    components: {
        CustomSearchResourceSelect,
        SimpleSelect,
        RelativeDateRange,
        AbsoluteDateRange,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            hasVotedOptions: [],
            importanceOptions: [],
            timeOptions: [],
        };
    },
    computed: {
        ideaOptions () {
            if (this.loadingResources) return [];

            return [
                {
                    label: 'default',
                    options: [
                        {
                            name: this.$t('ideaRule.Any Idea'),
                            tooltip: { content: this.$t('ideaRule.Any Idea') },
                            ideaId: '*',
                        },
                    ],
                },
                {
                    label: 'divider',
                    options: this.resources.reduce((acc, idea) => {
                        acc.push({
                            name: idea.formData[0].content,
                            tooltip: {
                                content: idea.formData[0].content,
                            },
                            ideaId: idea.id,
                        });

                        return acc;
                    }, []),
                },
            ];
        },
        selectedIdea () {
            return this.ideaOptions
                .reduce((acc, option) => {
                    return acc.concat(option.options);
                }, [])
                .find((idea) => idea.ideaId === this.rule.ideaId);
        },
        selectedHasVoted () {
            if (this.rule.hasVoted !== undefined && this.rule.hasVoted !== null) {
                return this.hasVotedOptions.find((vote) => vote.hasVoted === this.rule.hasVoted);
            }

            this.$emit('input', { field: 'hasVoted', value: this.hasVotedOptions[0].hasVoted });

            return this.hasVotedOptions[0];
        },
        selectedImportance () {
            if (this.rule.importance) {
                return this.importanceOptions.find((importance) => importance.importance === this.rule.importance);
            }

            const defaultImportance = 'any';
            this.$emit('input', { field: 'importance', value: defaultImportance });

            return this.importanceOptions.find((importance) => importance.importance === defaultImportance);
        },
        selectedTime () {
            if (this.rule.time) {
                return this.timeOptions.find((time) => time.time === this.rule.time);
            }

            this.$emit('input', { field: 'time', value: this.timeOptions[0].time });

            return this.timeOptions[0];
        },
        dateComponent () {
            if (this.rule.time === 'withinLast') {
                return getRelativeDateRangeConfig(this.rule);
            }
            if (this.rule.time === 'between') {
                return getAbsoluteDateRangeConfig(this.rule);
            }

            return false;
        },
    },
    created () {
        this.hasVotedOptions = [
            { hasVoted: true, name: this.$t('ideaRule.Has voted') },
            { hasVoted: false, name: this.$t('ideaRule.Has not voted') },
        ];
        this.importanceOptions = [
            { importance: 'any', name: this.$t('ideaRule.Any importance') },
            { importance: 'positive', name: this.$t('ideaRule.Must have or Nice to have') },
            { importance: '2', name: this.$t('ideaRule.Must have') },
            { importance: '1', name: this.$t('ideaRule.Nice to have') },
            { importance: '-1', name: this.$t('ideaRule.Not interested') },
        ];
        this.timeOptions = [
            { time: 'ever', name: this.$t('ideaRule.ever') },
            { time: 'withinLast', name: this.$t('ideaRule.within last') },
            { time: 'between', name: this.$t('ideaRule.between') },
        ];
    },
    methods: {
        filterIdeas ({ query, options }) {
            const normalizedSearch = query.toLowerCase().trim();

            options[1].options = options[1].options.filter((option) => {
                for (const key of ['name']) {
                    const normalizedOptionKey = option[key].toString().toLowerCase();

                    if (normalizedOptionKey.includes(normalizedSearch)) {
                        return true;
                    }
                }

                return false;
            });

            return options;
        },
    },
};
</script>

<style scoped lang="less">
.idea-resource-select__header {
    height: 40px;
    border-bottom: 1px solid var(--palette-gray-30);
    display: flex;
    align-items: center;

    .idea-resource-select__selected-option {
        padding: 0 16px;
        flex: 1;
        cursor: pointer;
        width: 100%;
        line-height: 26px;

        .pendo-multiselect-option--actions {
            position: static;
        }

        &:hover {
            background-color: var(--palette-gray-20);
        }
    }
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
